import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { PERIOD_LIST_ALL_QUERY_KEY } from '~/services/period';
import type { Period } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { QUERY_KEY as USER_PROFILE_QUERY_KEY } from '~/services/useUserProfileQuery';

const log = createLogger('usePeriodAddMutation');

export type AddPeriodRequest = {
  name: string;
  shortName: string;
  voiceNames: string[];
};

export type AddPeriodResponse = {
  period?: Period;
};

export function usePeriodAddMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: AddPeriodRequest) => {
      log.debug('Adding period', { request });
      const { period } = await httpClient.post<AddPeriodResponse>('/period/add', request);
      if (period) {
        log.info('Added period successfully', {
          periodId: period.id,
          periodName: sanitizeTagValue(period.name),
          periodShortName: sanitizeTagValue(period.shortName),
        });
      } else {
        log.info('Added period - no changes were made');
      }
      return { period };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PERIOD_LIST_ALL_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_PROFILE_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to add period', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
